import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { AdminApiService } from '@services/adminApiService';
import { LanguageCode } from '@models/languageCode';
import { FeatureCard, FeatureCardSelectItem, FeatureCardWithType, ResourceHubContentType } from '@models/section/featureCardSection';
import { PageContentType } from '@models/page';
import { PublicApiService } from '@services/publicApiService';

export const defaultFeatureCardItemKey = 'featureCardItems';
export const defaultFeatureCardKey = 'featureCard';

export function useFeatureCardItems(type: ResourceHubContentType, languageCode: LanguageCode, enabled = true): UseQueryResult<FeatureCardSelectItem[]> {
    const featureCardQueryMapping: Record<ResourceHubContentType, ((value: string) => Promise<FeatureCardSelectItem[]>) | null> = {
        [ResourceHubContentType.Blog]: AdminApiService.blogAdminService.getFeaturedCardSelectItems,
        [ResourceHubContentType.Glossary]: AdminApiService.glossaryAdminService.getFeaturedCardSelectItems,
        [ResourceHubContentType.SuccessStory]: AdminApiService.successStoryAdminService.getFeaturedCardSelectItems,
        [ResourceHubContentType.PressRelease]: AdminApiService.pressReleaseAdminService.getFeaturedCardSelectItems,
        [ResourceHubContentType.WhitePaper]: AdminApiService.whitePaperAdminService.getFeaturedCardSelectItems,
        [ResourceHubContentType.Video]: AdminApiService.videoAdminService.getFeaturedCardSelectItems,
        [ResourceHubContentType.Ebook]: () => AdminApiService.pageAdminService.getFeaturedCardSelectItems(languageCode, PageContentType.EBook),
        [ResourceHubContentType.Webinar]: () => AdminApiService.pageAdminService.getFeaturedCardSelectItems(languageCode, PageContentType.Webinar),
        [ResourceHubContentType.Podcast]: () => AdminApiService.pageAdminService.getFeaturedCardSelectItems(languageCode, PageContentType.Podcast),
        [ResourceHubContentType.Guides]: () => AdminApiService.pageAdminService.getFeaturedCardSelectItems(languageCode, PageContentType.Guides),
        [ResourceHubContentType.Tools]: () => AdminApiService.pageAdminService.getFeaturedCardSelectItems(languageCode, PageContentType.Tools),
    };

    const featureCardQueryFn = featureCardQueryMapping[type];

    return useQuery({
        queryKey: [defaultFeatureCardItemKey, type, languageCode],
        queryFn: featureCardQueryFn ? () => featureCardQueryFn(languageCode) : undefined,
        enabled: enabled && !!featureCardQueryFn,
    });
}

export function useFeatureCards(featureCardItems: FeatureCard[], enabled = true): UseQueryResult<FeatureCardWithType[]> {

    const createFeatureCardWithType = async (item: FeatureCard): Promise<FeatureCardWithType | undefined> => {
        const serviceMapping: Record<ResourceHubContentType, () => Promise<unknown>> = {
            [ResourceHubContentType.Blog]: () => PublicApiService.blogPublicService.getById(item.id),
            [ResourceHubContentType.Video]: () => PublicApiService.videoPublicService.getById(item.id),
            [ResourceHubContentType.Glossary]: () => PublicApiService.glossaryPublicService.getById(item.id),
            [ResourceHubContentType.SuccessStory]: () => PublicApiService.successStoryPublicService.getById(item.id),
            [ResourceHubContentType.PressRelease]: () => PublicApiService.pressReleasePublicService.getById(item.id),
            [ResourceHubContentType.WhitePaper]: () => PublicApiService.whitePaperPublicService.getById(item.id),
            [ResourceHubContentType.Ebook]: () => PublicApiService.pagePublicService.getById(item.id),
            [ResourceHubContentType.Webinar]: () => PublicApiService.pagePublicService.getById(item.id),
            [ResourceHubContentType.Podcast]: () => PublicApiService.pagePublicService.getById(item.id),
            [ResourceHubContentType.Guides]: () => PublicApiService.pagePublicService.getById(item.id),
            [ResourceHubContentType.Tools]: () => PublicApiService.pagePublicService.getById(item.id),
        };

        const service = serviceMapping[item.contentType];
        if (!service) return undefined;

        const result = await service();
        if (!result) return undefined;

        return { ...result, type: item.contentType } as FeatureCardWithType;
    };

    const validFeatureCardItems = featureCardItems.filter(item => item.id !== -1 && item.id > 0);

    return useQuery({
        queryKey: [defaultFeatureCardKey, validFeatureCardItems.map(item => `${item.contentType}-${item.id}`).join(',')],
        queryFn: async (): Promise<FeatureCardWithType[]> => {
            const promises = validFeatureCardItems.map(item => createFeatureCardWithType(item));
            const results = await Promise.all(promises);
            return results.filter((result) => result !== undefined);
        },
        enabled: enabled && validFeatureCardItems.length > 0,
    });
}